import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Container,
  Box,
  Grid,
  Button
} from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  colors,
  Section,
  GradientText,
  containerVariants,
  itemVariants
} from '../../styled/StyledComponents';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { styled } from '@mui/material/styles';

// Styled components

const VideoContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  minHeight: '450px',
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.4))',
    pointerEvents: 'none',
  },
  [theme.breakpoints.down('md')]: {
    minHeight: '350px',
  },
}));

const PlayButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  bottom: '20px',
  right: '20px',
  zIndex: 10,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  color: colors.primary,
  borderRadius: '50%',
  minWidth: '56px',
  width: '56px',
  height: '56px',
  padding: 0,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
  '&:hover': {
    backgroundColor: 'white',
    transform: 'scale(1.1)',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.25)',
  },
  transition: 'all 0.3s ease',
}));

const HighlightText = styled(Box)({
  position: 'relative',
  display: 'inline-block',
  color: colors.primary,
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '5px',
    left: 0,
    width: '100%',
    height: '8px',
    background: 'rgba(12, 132, 102, 0.2)',
    zIndex: -1,
    borderRadius: '4px',
  },
});

const MissionSection = forwardRef(({ inView }, ref) => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(false);


  const togglePlayPause = () => {
    const video = document.getElementById('mission-video');
    if (video) {
      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoLoad = () => {
    setVideoLoaded(true);
  };

  return (
    <Section id="mission-section" ref={ref}>
      <Container maxWidth="xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <GradientText variant="overline" sx={{ 
                  mb: 2, 
                  letterSpacing: 8, 
                  fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                  display: 'inline-block',
                }}>
                  {t('landing.mission.header')}
                </GradientText>
                <Typography
                  variant="h2"
                  sx={{
                    color: colors.text,
                    fontWeight: 800,
                    mb: 3,
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3.2rem', lg: '3.5rem' },
                    lineHeight: 1.2,
                    position: 'relative',
                  }}
                >
                  {t('landing.mission.title.prefix')}{' '}
                  <HighlightText>
                    {t('landing.mission.title.highlight')}
                  </HighlightText>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: colors.textLight,
                    fontWeight: 400,
                    mb: 4,
                    lineHeight: 1.8,
                    maxWidth: '600px',
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                  }}
                >
                  {t('landing.mission.description')}
                </Typography>
                
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div
                variants={itemVariants}
                style={{ height: '100%' }}
              >
                <VideoContainer>
                  <Box
                    component="video"
                    id="mission-video"
                    autoPlay
                    muted
                    loop={isPlaying}
                    playsInline
                    onLoadedData={handleVideoLoad}
                    sx={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '16px',
                      opacity: videoLoaded ? 1 : 0,
                      transition: 'opacity 0.5s ease',
                    }}
                  >
                    <source src="/videos/biochar.mp4" type="video/mp4" />
                  </Box>
                  
                  {/* Video overlay content */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: '30px',
                      left: '30px',
                      zIndex: 10,
                      maxWidth: '70%',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        mb: 1,
                      }}
                    >
                      {t('landing.mission.video.title')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255,255,255,0.9)',
                        textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                        display: { xs: 'none', sm: 'block' },
                      }}
                    >
                      {t('landing.mission.video.description')}
                    </Typography>
                  </Box>
                  
                  <PlayButton
                    onClick={togglePlayPause}
                    aria-label={isPlaying ? t('landing.mission.video.pause') : t('landing.mission.video.play')}
                  >
                    {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                  </PlayButton>
                </VideoContainer>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Section>
  );
});

MissionSection.displayName = 'MissionSection';

MissionSection.propTypes = {
  /**
   * Boolean indicating if the section is in view
   * Used for triggering animations
   */
  inView: PropTypes.bool.isRequired,
};

export default MissionSection;
